import type { IndexRange, ShiftClickRange } from './TrackList.types'

/**
 * Pure helpers for shift-click range selection in the track list: an anchor row
 * plus movable start/end index bounds, updated by shift-click. Turns those
 * bounds into the selected track array. No React - consumed by
 * TrackList.hooks.ts. Types live in TrackList.types.ts. Unit-tested in
 * TrackList.utils.test.ts for mergeShiftRangeIntoSelection edge cases;
 * shift-click range updates are covered by track-list-selection.spec.js.
 *
 * Mental model:
 * - anchorIndex - row where the shift gesture started (stays fixed)
 * - startIndex / endIndex - inclusive row bounds of the current range
 * - rangeTracks - tracks[startIndex..endIndex]
 * - tracksSelected - all checked tracks (range tracks + any picked outside the range)
 */

/** Get the IDs of the tracks in the given range. */
const idsInRange = (
	tracks: Track[],
	{ startIndex, endIndex }: IndexRange
): number[] => tracks.slice(startIndex, endIndex + 1).map(({ id }) => id)

/** Start a fresh range collapsed onto a single row. */
export const createShiftRangeAt = (index: number): ShiftClickRange => ({
	anchorIndex: index,
	startIndex: index,
	endIndex: index,
})

/** Shift-click: `index >= anchorIndex` moves the end index; otherwise moves the start index. */
export const extendShiftRangeTo = (
	range: ShiftClickRange,
	index: number
): ShiftClickRange =>
	index >= range.anchorIndex
		? { ...range, endIndex: index }
		: { ...range, startIndex: index }

/**
 * Recomputes selection after a shift range changes: selection =
 * (current - previousRangeTracks) + newRangeTracks. Shrinking deselects rows;
 * individually picked tracks outside the range stay selected. No duplicate ids.
 */
export const mergeShiftRangeIntoSelection = (
	selectedTracks: Track[],
	trackList: Track[],
	previousRange: IndexRange | null,
	newRange: IndexRange
): Track[] => {
	const rangeTracks = trackList.slice(
		newRange.startIndex,
		newRange.endIndex + 1
	)
	const rangeTrackIds = new Set(rangeTracks.map(({ id }) => id))

	const removedIds = new Set(
		previousRange ? idsInRange(trackList, previousRange) : []
	)
	const tracksToKeep = selectedTracks.filter(
		({ id }) => !removedIds.has(id) || rangeTrackIds.has(id)
	)

	const currentSelectedTrackIds = new Set(tracksToKeep.map(({ id }) => id))
	const updatedSelectedTracks = [...tracksToKeep]

	rangeTracks.forEach((track) => {
		if (!currentSelectedTrackIds.has(track.id)) {
			currentSelectedTrackIds.add(track.id)
			updatedSelectedTracks.push(track)
		}
	})

	return updatedSelectedTracks
}
